///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectorRef, Directive, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { Principal } from './principal.service';
import { UserAuthorities } from '../../services/user/user-authorities';
import { Subscription } from 'rxjs';

/**
 * @whatItDoes Conditionally includes an HTML element if current user has any
 * of the authorities passed as the `expression`.
 *
 * @howToUse
 * ```
 *     <some-element *windeHasAnyAuthority="'ROLE_ADMIN'">...</some-element>
 *
 *     <some-element *windeHasAnyAuthority="['ROLE_ADMIN', 'ROLE_USER']">...</some-element>
 * ```
 */
@Directive({
    selector: '[windeHasAnyAuthority]'
})
export class HasAnyAuthorityDirective implements OnDestroy {
    private authorities: string[];
    private stateSubscription: Subscription;

    constructor(
        private readonly principal: Principal,
        private readonly templateRef: TemplateRef<any>,
        private readonly viewContainerRef: ViewContainerRef,
        private readonly changeDetection: ChangeDetectorRef
    ) {}

    @Input()
    set windeHasAnyAuthority(value: string | string[]) {
        if (value === UserAuthorities.ANY_USER) {
            this.viewContainerRef.clear();
            this.viewContainerRef.createEmbeddedView(this.templateRef);
            this.changeDetection.markForCheck();
        } else {
            this.authorities = typeof value === 'string' ? [value] : value;
            this.updateView();
            // Get notified each time authentication state changes.
            this.stateSubscription = this.principal.getAuthenticationState().subscribe((_identity) => this.updateView());
        }
    }

    ngOnDestroy(): void {
        if (this.stateSubscription) {
            this.stateSubscription.unsubscribe();
            this.stateSubscription = null;
        }
        this.authorities = null;
    }

    private updateView(): void {
        if (this.authorities[0] === UserAuthorities.ANONYMOUS) {
            this.viewContainerRef.clear();

            if (!this.principal.isAuthenticated()) {
                this.viewContainerRef.createEmbeddedView(this.templateRef);
                this.changeDetection.markForCheck();
            }
        } else {
            this.principal.hasAnyAuthority(this.authorities).then((result) => {
                this.viewContainerRef.clear();
                if (result) {
                    this.viewContainerRef.createEmbeddedView(this.templateRef);
                    this.changeDetection.markForCheck();
                }
            });
        }
    }
}
